/* eslint-disable @typescript-eslint/no-explicit-any */
import { AxiosResponse, ResponseType } from 'axios';
import {
  adcApi,
  adcRefreshableApi,
  privateApi,
  privateMultipartFormDataRefreshableApi,
  privateRefreshableApi,
} from '@configs/axios-instances';
import {
  IVideobiteSignedURLParams,
  IVideobiteSignedURLResponse,
  ICreateVideobitePayload,
  IUpdateVideobiteParams,
  GetVideobitesParamsType,
} from '@app/types/Videobites';
import { GroupType, IShow } from '@insights_module/types/Show';
import { PaginatedResponse } from '@app/types/common';
import { IEpisode } from '@insights_module/types/Episode';
import { BrandSafetySummaryResponse, GarmResponse } from '@insights_module/types/BrandSafety';
import {
  ContextualResponse,
  ContextualSummaryForEpisode,
  LinkedNamedEntitySummaryForEpisodeResponse,
  NamedEntityForEpisodeResponse,
  NamedEntityShowResponse,
  NamedEntitySummaryForEpisodeResponse,
} from '@insights_module/types/ContextualAndEntity';
import {
  ContextCategoriesResponse,
  ContextualTraceabilityTypeResponse,
  GarmTraceabilityTypeResponse,
  IabTraceabilityTypeResponse,
} from '@insights_module/types/Traceability';
import {
  AudioChaptersResponse,
  Episode,
  EpisodeKeywordsResponse,
  EpisodeTopicsResponse,
  GenerateAIContentForEpisodePayload,
} from '@app/types/ADCTypes';
import { GetAllVGeneratedContentParams } from '@app/types/GenAI';

////////////////// adc groups

/**
 * Fetch show details
 * @param {string} id
 * @return {Promise}
 */

export const fetchShow = async (id: string): Promise<IShow> => {
  const { data } = await adcApi.GET.get(`/groups/${id}`);

  return data as IShow;
};

/**
 * Search groups with "adcQL" params
 * @param {unknown} payload
 * @return {Promise}
 */

export const searchGroups = async ({
  page,
  pageSize,
  payload,
}: {
  page: number;
  pageSize: number;
  payload: unknown;
}): Promise<PaginatedResponse<GroupType>> => {
  return (await adcApi.POST.post(
    `/groups/search?page_no=${page}&page_size=${pageSize}`,
    payload
  )) as PaginatedResponse<GroupType>;
};

/**
 * Fetch audio list for group by groupId
 * @param {string} groupId
 * @return {Promise}
 */

export const fetchAudiosByGroupId = async (
  groupId: string
): Promise<PaginatedResponse<IEpisode>> => {
  return (await adcApi.GET.get(`/groups/${groupId}/audio`)) as PaginatedResponse<IEpisode>;
};

/**
 * Fetch group brand safety GARM result
 * @param {string} id
 * @return {Promise}
 */

export const fetchGroupGarmResults = async (id: string): Promise<GarmResponse> => {
  const { data } = (await adcApi.GET(
    `/groups/${id}/brand-safety/garm`
  )) as AxiosResponse<GarmResponse>;

  return data;
};

/**
 * Fetch contextual data for group by groupId
 * @param {string} id
 * @return {Promise}
 */

export const fetchContextualForShow = async (id: string): Promise<ContextualResponse> => {
  const { data } = (await adcApi.GET(
    `/groups/${id}/contextual`
  )) as AxiosResponse<ContextualResponse>;

  return data;
};

/**
 * Fetch name entities for group by groupId
 * @param {string} id
 * @param {unknown} params
 * @return {Promise}
 */

export const fetchEntitiesForShow = async (
  id: string,
  params: unknown
): Promise<NamedEntityShowResponse> => {
  const { data } = (await adcApi.GET(`/groups/${id}/named-entities`, {
    params,
  })) as AxiosResponse<NamedEntityShowResponse>;

  return data;
};

////////////////// adc audio

/**
 * Fetch garm brand safety for episode by id
 * @param {string} id
 * @return {Promise}
 */

export const fetchGarmBrandSafetyForEpisode = async (id: string): Promise<GarmResponse> => {
  const { data } = (await adcApi.GET.get(
    `/audio/${id}/brand-safety/garm`
  )) as AxiosResponse<GarmResponse>;

  return data;
};

/**
 * Fetch audio summary with "adcQL" params
 * @param {unknown} payload
 * @return {Promise}
 */

export const fetchAudioBrandSafetySummary = async (
  payload: unknown
): Promise<BrandSafetySummaryResponse> => {
  const { data } = (await adcApi.POST.post(
    '/audio/summary/brand-safety',
    payload
  )) as AxiosResponse<BrandSafetySummaryResponse>;

  return data;
};

/**
 * Fetch audio named entities summary with "adcQL" params
 * @param {unknown} payload
 * @return {Promise}
 */

export const fetchAudioNamedEntitiesSummary = async (
  payload: unknown
): Promise<NamedEntitySummaryForEpisodeResponse> => {
  const { data } = (await adcApi.POST.post(
    '/audio/summary/named-entities',
    payload
  )) as AxiosResponse<NamedEntitySummaryForEpisodeResponse>;

  return data;
};

/**
 * Fetch audio named entities summary with "adcQL" params
 * @param {unknown} payload
 * @return {Promise}
 */

export const fetchAudioLinkedNamedEntitiesSummary = async (
  payload: unknown
): Promise<LinkedNamedEntitySummaryForEpisodeResponse> => {
  const { data } = (await adcApi.POST.post('/audio/summary/linked-named-entities', payload, {
    params: { top_entities_no: 500 },
  })) as AxiosResponse<LinkedNamedEntitySummaryForEpisodeResponse>;

  return data;
};

/**
 * Fetch audio contextual summary with "adcQL" params
 * @param {unknown} payload
 * @return {Promise}
 */

export const fetchAudioContextualSummary = async (
  payload: unknown
): Promise<ContextualSummaryForEpisode[]> => {
  const { data } = (await adcApi.POST.post('/audio/summary/contextual', payload)) as AxiosResponse<
    ContextualSummaryForEpisode[]
  >;

  return data;
};

/**
 * Fetch chapters for audio
 * @param {string} id
 * @return {Promise}
 */

export const fetchChaptersForEpisode = async (id: string): Promise<AudioChaptersResponse> => {
  const { data } = (await adcApi.GET.get(
    `/audio/${id}/chapters`
  )) as AxiosResponse<AudioChaptersResponse>;

  return data;
};

/**
 * Fetch contextual for audio
 * @param {string} id
 * @return {Promise}
 */

export const fetchContextualForEpisode = async (id: string): Promise<ContextCategoriesResponse> => {
  const { data } = (await adcApi.GET.get(
    `/audio/${id}/contextual`
  )) as AxiosResponse<ContextCategoriesResponse>;

  return data;
};

/**
 * Fetch name entities for audio
 * @param {string} id
 * @return {Promise}
 */

export const fetchEntitiesForEpisode = async (
  id: string
): Promise<NamedEntityForEpisodeResponse> => {
  const { data } = (await adcApi.GET.get(
    `/audio/${id}/named-entities`
  )) as AxiosResponse<NamedEntityForEpisodeResponse>;

  return data;
};

/**
 * Fetch topics for audio
 * @param {string} id
 * @return {Promise}
 */

export const fetchTopicsForEpisode = async (id: string): Promise<EpisodeTopicsResponse> => {
  const { data } = (await adcApi.GET.get(
    `/audio/${id}/topics`
  )) as AxiosResponse<EpisodeTopicsResponse>;

  return data;
};

/**
 * Fetch topics for audio
 * @param {string} id
 * @return {Promise}
 */

export const fetchKeywordsForEpisode = async (id: string): Promise<EpisodeKeywordsResponse> => {
  const { data } = (await adcApi.GET.get(
    `/audio/${id}/keywords`
  )) as AxiosResponse<EpisodeKeywordsResponse>;

  return data;
};

/**
 * Fetch audio list for group
 * @param {pageParam: number, pageSize: number, payload: unknown}
 * @return {Promise}
 */

export const searchAudios = async ({
  page,
  pageSize,
  payload,
}: {
  page: number;
  pageSize: number;
  payload: unknown;
}): Promise<PaginatedResponse<Episode>> => {
  return (await adcApi.POST.post(
    `/audio/search?page_no=${page}&page_size=${pageSize}`,
    payload
  )) as PaginatedResponse<Episode>;
};

/**
 * Fetch brand safety GARM with traceability for episode by id
 * @param {string} id
 * @return {Promise}
 */

export const fetchGarmTraceabilityForEpisode = async (
  id: string
): Promise<GarmTraceabilityTypeResponse> => {
  const { data } = (await adcApi.GET.get(
    `/audio/${id}/brand-safety/garm/traceability`
  )) as AxiosResponse<GarmTraceabilityTypeResponse>;

  return data;
};

/**
 * Fetch brand safety IAB with traceability for episode by id
 * @param {string} id
 * @return {Promise}
 */

export const fetchIabTraceabilityForEpisode = async (
  id: string
): Promise<IabTraceabilityTypeResponse> => {
  const { data } = (await adcApi.GET.get(
    `/audio/${id}/brand-safety/iab/traceability`
  )) as AxiosResponse<IabTraceabilityTypeResponse>;

  return data;
};

/**
 * Fetch contextual with traceability for episode by id
 * @param {string} id
 * @return {Promise}
 */

export const fetchContextualTraceabilityForEpisode = async (
  id: string
): Promise<ContextualTraceabilityTypeResponse> => {
  const { data } = (await adcApi.GET.get(
    `/audio/${id}/contextual/traceability`
  )) as AxiosResponse<ContextualTraceabilityTypeResponse>;

  return data;
};

/**
 * Fetch transcript for episode by id
 * @param {string} id
 * @param {unknown} params
 * @return {Promise}
 */

export const fetchTranscriptForEpisode = async (
  id: string,
  params?: unknown,
  responseType?: ResponseType
): Promise<any> => {
  return await adcApi.GET.get(`/audio/${id}/transcript`, { params, responseType });
};

/**
 * Fetch diarized transcript for episode by id
 * @param {string} id
 * @param {unknown} params
 * @return {Promise}
 */

export const fetchDiarizedTranscriptForEpisode = async (
  id: string,
  params?: unknown
): Promise<any> => {
  return await adcApi.GET.get(`/audio/${id}/transcript/diarized`, { params });
};

/**
 * Fetch transcript for episode by id
 * @param {string} id
 * @return {Promise}
 */

export const fetchVTTTranscriptForEpisode = async (
  id: string,
  responseType?: ResponseType
): Promise<any> => {
  return await adcApi.GET.get(`/audio/${id}/vtt`, { responseType });
};

/**
 * Fetch audio details by id
 * @param {string} id
 * @return {Promise}
 */

export const fetchAudioDetails = async (id: string): Promise<any> => {
  return await adcApi.GET.get(`/audio/${id}`);
};

/**
 * Add episode to user library
 * @param {string} id
 * @return {Promise}
 */

export const addEpisodeToUserLibrary = async (id: number | string): Promise<any> => {
  return await privateRefreshableApi.post(`/library/episode/${id}`);
};

/**
 * Upload audio file to user library
 * @param {FormData} payload
 * @return {Promise}
 */

export const uploadAudioToUserLibrary = async (payload: FormData): Promise<any> => {
  return await privateMultipartFormDataRefreshableApi.post('/library/upload-audio', payload);
};

////////////////// exports

/**
 * Initiate CSV export
 * @param {unknown} payload
 * @return {Promise}
 */

export const initiateCSVExport = async (payload: unknown): Promise<any> => {
  return await adcRefreshableApi.POST.post('/export', payload);
};

/**
 * Fetch CSV export
 * @param {string} id
 * @return {Promise}
 */

export const fetchCSVExport = async (id: string): Promise<any> => {
  return await adcApi.GET.get(`/export/${id}`);
};

/**
 * Fetch sales avails
 * @param {unknown} payload
 * @return {Promise}
 */

export const fetchFlightpathSalesAvails = async (payload: unknown): Promise<any> => {
  return await adcApi.POST.post('/flightpath/sales-avails', payload);
};

////////////////// views  //TODO: check should we use this or use from audio insights

export const fetchViews = async (): Promise<any> => {
  return await privateApi.get('/views');
};

export const fetchView = async (id: string): Promise<any> => {
  return await privateApi.get(`/views/${id}`);
};

export const createView = async (payload: unknown): Promise<any> => {
  return await privateApi.post('/views/create', payload);
};

export const updateView = async (id: string, payload: any): Promise<any> => {
  return await privateApi.put(`/views/update/${id}`, payload);
};

export const deleteView = async (id: string): Promise<any> => {
  return await privateApi.delete(`/views/delete/${id}`);
};

////////////////// sounder index

export const fetchFeeds = async (): Promise<any> => {
  return await privateApi.get('content');
};

export const fetchFeed = async (id: number): Promise<any> => {
  return await privateApi.get(`content/feed/${id}`);
};

export const fetchFeedShow = async (id: number): Promise<any> => {
  return await privateApi.get(`content/feed/${id}/show`);
};

export const fetchEpisodesByContentId = async (id: number): Promise<any> => {
  return await privateApi.get(`content/show/${id}/episodes`);
};

////////////////// Gen AI @deprecated (use adc gen ai instead)

/**
 * Fetch generated data from episode transcript
 * @param {unknown} payload
 * @return {Promise}
 * @deprecated
 */

export const fetchGeneratedDataFromEpisodeTranscript = async (payload: any): Promise<any> => {
  return await privateApi.post('gen-ai/generate-audio-type-content', payload);
};

/**
 * Generate content for resource type from prompt
 * @param {unknown} payload
 * @return {Promise}
 * @deprecated
 */

export const generateContent = async (payload: any): Promise<any> => {
  return await privateApi.post('gen-ai/generate-content', payload, { timeout: 180000 });
};

/**
 * Generate new content based on previous content and prompt
 * @param {unknown} payload
 * @return {Promise}
 * @deprecated
 */

export const generateContentFromPreviousConversation = async (payload: any): Promise<any> => {
  return await privateApi.post('gen-ai/chat', payload, { timeout: 180000 });
};

/**
 * Set generated content as default for type and episode
 * @param {number} content_id
 * @return {Promise}
 * @deprecated
 */

export const setGeneratedContentAsDefault = async (content_id: number): Promise<any> => {
  return await privateRefreshableApi.put(`gen-ai/generative-content/${content_id}`);
};

/**
 * Get all generated ai content
 * @param {GetAllVGeneratedContentParams} params
 * @return {Promise}
 * @deprecated
 */

export const getAllVGeneratedContent = async (
  params: GetAllVGeneratedContentParams
): Promise<any> => {
  return await privateApi.get('/gen-ai/generative-content', { params });
};

/**
 * Get all videobites
 * @param {GetVideobitesParamsType} params
 * @param {GetVideobitesParamsType} params
 * @return {Promise}
 * @deprecated
 */

export const getAllVideobites = async (params: GetVideobitesParamsType): Promise<any> => {
  return await privateApi.get('/gen-ai/generative-content', { params });
};

/**
 * Get videobites for episode id
 * @param {string} id
 * @param {GetVideobitesParamsType} params
 * @return {Promise}
 * @deprecated
 */

export const getEpisodeVideobites = async (
  id: string | undefined,
  params: GetVideobitesParamsType
): Promise<any> => {
  return await privateApi.get(`/gen-ai/generative-content/${id}`, { params });
};

/**
 * Generate video content
 * @param {unknown} payload
 * @return {Promise}
 * @deprecated
 */

export const generateVideoContent = async (payload: any): Promise<any> => {
  return await privateApi.post('gen-ai/generate-video-content', payload, { timeout: 180000 });
};

/**
 * Get signed url for creating Videobite
 * @param {IGetCreateVideobiteSignedUrlParams} payload
 * @return {Promise}
 * @deprecated
 */

export const getVideobiteSignedURL = async (
  payload: IVideobiteSignedURLParams
): Promise<IVideobiteSignedURLResponse | undefined> => {
  const { data } = await privateRefreshableApi.post('gen-ai/file-upload', payload);

  return data;
};

/**
 * Generate Videobite
 * @param {ICreateVideobitePayload} payload
 * @return {Promise}
 * @deprecated
 */

export const generateVideobite = async (payload: ICreateVideobitePayload): Promise<any> => {
  return await privateRefreshableApi.post('gen-ai/generate-video-content', payload, {
    timeout: 180000,
  });
};

/**
 * Update Videobite
 * @param {string} id
 * @param {IUpdateVideobiteParams} payload
 * @return {Promise}
 * @deprecated
 */

export const updateVideobite = async (
  id: number,
  payload: IUpdateVideobiteParams
): Promise<any> => {
  return await privateRefreshableApi.put(`gen-ai/generate-video-content/${id}`, payload);
};

/**
 * Update Videobite
 * @param {string} id
 * @return {Promise}
 * @deprecated
 */

export const deleteVideobite = async (id: number): Promise<any> => {
  return await privateRefreshableApi.delete(`gen-ai/generative-content/${id}`);
};

/**
 * Update Videobite by id
 * @param {string} id
 * @return {Promise}
 * @deprecated
 */

export const fetchVideobiteById = async (id: number): Promise<any> => {
  return await privateApi.get(`gen-ai/generative-content/${id}`);
};

/**
 * Duplicate Videobite
 * @param {string} id
 * @param {any} payload
 * @return {Promise}
 * @deprecated
 */

export const duplicateVideobite = async (id: number, payload: any): Promise<any> => {
  return await privateRefreshableApi.post(`gen-ai/generate-video-content/${id}/duplicate`, payload);
};

////////////////// adc gen ai

/**
 * Generate ai content for episode
 * @param {string} id
 * @param {GenerateAIContentForEpisodePayload} payload
 * @return {Promise}
 */

export const generateAIContentForEpisode = async (
  id: string,
  payload: GenerateAIContentForEpisodePayload
): Promise<any> => {
  return await adcApi.POST.post(`audio/${id}/gen-ai/resources`, payload);
};

/**
 * Fetch linkedin generated data for episode
 * @param {string} id
 * @return {Promise}
 */

export const fetchLinkedinGeneratedData = async (id: string): Promise<any> => {
  return await adcApi.GET.get(`/audio/${id}/gen-ai/linkedin-post`);
};

/**
 * Fetch instagram generated data for episode
 * @param {string} id
 * @return {Promise}
 */

export const fetchInstagramGeneratedData = async (id: string): Promise<any> => {
  return await adcApi.GET.get(`/audio/${id}/gen-ai/instagram-post`);
};

/**
 * Fetch twitter generated data for episode
 * @param {string} id
 * @return {Promise}
 */

export const fetchTwitterGeneratedData = async (id: string): Promise<any> => {
  return await adcApi.GET.get(`/audio/${id}/gen-ai/twitter-post`);
};

/**
 * Fetch facebook generated data for episode
 * @param {string} id
 * @return {Promise}
 */

export const fetchFacebookGeneratedData = async (id: string): Promise<any> => {
  return await adcApi.GET.get(`/audio/${id}/gen-ai/facebook-post`);
};

/**
 * Fetch show notes generated data for episode
 * @param {string} id
 * @return {Promise}
 */

export const fetchShowNotesGeneratedData = async (id: string): Promise<any> => {
  return await adcApi.GET.get(`/audio/${id}/gen-ai/show-notes`);
};

/**
 * Fetch article generated data for episode
 * @param {string} id
 * @return {Promise}
 */

export const fetchArticleGeneratedData = async (id: string): Promise<any> => {
  return await adcApi.GET.get(`/audio/${id}/gen-ai/article`);
};

/**
 * Fetch summary generated data for episode
 * @param {string} id
 * @return {Promise}
 */

export const fetchSummaryGeneratedData = async (id: string): Promise<any> => {
  return await adcApi.GET.get(`/audio/${id}/gen-ai/summary`);
};

////////////////// adc groups proxy

/**
 * Search shows list
 * @param {unknown} payload
 * @return {Promise}
 */

export const searchADCShows = async (payload: unknown): Promise<any> => {
  return await privateApi.post('adc/search-groups', payload);
};

/**
 * Fetch episodes for show
 * @param {string} id
 * @return {Promise}
 */

export const fetchADCEpisodesForShow = async (id: string): Promise<any> => {
  return await privateApi.get(`adc/group/${id}/audio`);
};

////////////////// adc audio proxy

/**
 * Fetch audio list for group
 * @param {unknown} payload
 * @return {Promise}
 */

export const searchADCAudios = async (payload: unknown): Promise<any> => {
  return await privateApi.post('adc/search-audio', payload);
};

////////////////// integrations

/**
 * Fetch megaphone campaigns
 * @return {Promise}
 */

export const fetchMegaphoneCampaigns = async ({
  page = 1,
  pageSize = 500,
  keyword = '',
}: {
  page: number;
  pageSize: number;
  keyword?: string;
}): Promise<any> => {
  return await privateApi.get(
    `megaphone-api/campaigns?page=${page}&per_page=${pageSize}&keyword=${keyword}`
  );
};

/**
 * Fetch megaphone campaign orders
 * @param {string} id
 * @return {Promise}
 */

export const fetchMegaphoneCampaignOrders = async ({
  id,
  page = 1,
  pageSize = 500,
  keyword = '',
}: {
  id: string;
  page: number;
  pageSize: number;
  keyword?: string;
}): Promise<any> => {
  return await privateApi.get(
    `megaphone-api/campaigns/${id}/orders?page=${page}&per_page=${pageSize}&keyword=${keyword}`
  );
};

/**
 * Fetch tracking pixels
 * @return {Promise}
 */

export const fetchTrackingPixels = async ({
  page = 1,
  pageSize = 500,
}: {
  page: number;
  pageSize: number;
}): Promise<any> => {
  return await privateApi.get(`tracking-pixel?page=${page}&per_page=${pageSize}`);
};

/**
 * Create tracking pixel
 * @param {unknown} payload
 * @return {Promise}
 */

export const createTrackingPixel = async (payload: unknown): Promise<any> => {
  return await privateRefreshableApi.post('tracking-pixel/set', payload);
};

////////////////// Linked named entities

/**
 * Search linked named entities
 * @param {unknown} payload
 * @return {Promise}
 */
export const searchLinkedNamedEntities = async (payload: string): Promise<any> => {
  return await adcApi.GET.get(`/linked-named-entities?search_term=${payload}`);
};

/**
 * Fetch linked named entities by id or ids
 * @param {string} payload
 * @return {Promise}
 */
export const fetchLinkedNamedEntitiesById = async (payload: string): Promise<any> => {
  const _payload = payload.split(',').length - 1 > 1 ? `ids=${payload}` : payload;

  return await adcApi.GET.get(`/linked-named-entities/${_payload}`);
};

/**
 * Fetch linked named entities for audio
 * @param {string} id
 * @return {Promise}
 */
export const fetchAudioLinkedNamedEntities = async (id: string | undefined): Promise<any> => {
  return await adcApi.GET.get(`/audio/${id}/linked-named-entities`);
};
